import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import type { ProvenanceMap } from '@/modules/inventory/types/boat.types';
import { sameValue, type EditableBoat } from '@/modules/inventory/utils/boat-changes';

/**
 * #1178 (review I4) — copy the field-sources projection into the form's
 * `_provenance` subtree (never form VALUES). A field is skipped only while it
 * holds an unsaved local change (its value differs from the snapshot), so a
 * refetch cannot undo the dealer's in-progress "Edited by you" chip. Once the
 * change is saved it comes back as the server's `edited` row, and a later flag
 * on it (a photo conflict) or a dismissal reaches the field.
 */
export function useProvenanceSync(
  methods: Pick<UseFormReturn<WizardFormData>, 'getValues' | 'setValue'>,
  fieldSources: ProvenanceMap | undefined,
  getSnapshot: () => EditableBoat | null,
): void {
  useEffect(() => {
    if (!fieldSources) return;
    const saved = getSnapshot()?.scalars;
    const values = methods.getValues() as unknown as Record<string, unknown>;
    for (const [field, entry] of Object.entries(fieldSources)) {
      if (saved && field in saved && !sameValue(values[field], saved[field])) continue;
      // `as never` — `_provenance` is runtime-only form state (WizardFormData does not declare it).
      methods.setValue(`_provenance.${field}` as never, entry as never, { shouldDirty: false });
    }
    // Runs when the projection changes; the snapshot and the form are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldSources]);
}
